/**
 * Capacitor Plugin Initialization
 *
 * Initializes and configures all Capacitor plugins on app startup.
 * Call initializeCapacitor() in your main.tsx or App.tsx
 */

import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { isNative, isAndroid, isIOS, platform, logPlatformInfo } from '@/utils/platform';
import { setAppHeight, resetStrayDocumentScroll } from '@/utils/viewport';
import { initializePushNotifications, PushNotificationHandlers } from './pushNotifications';

// Re-export push notification service
export * from './pushNotifications';

/**
 * Initialize all Capacitor plugins
 * Call this once when the app starts
 */
export async function initializeCapacitor(options?: {
  pushHandlers?: PushNotificationHandlers;
}): Promise<void> {
  console.log('[Capacitor] Initializing plugins...');
  logPlatformInfo();

  if (!isNative) {
    console.log('[Capacitor] Running on web, skipping native plugin initialization');
    return;
  }

  try {
    // Hide splash screen with fade
    await SplashScreen.hide({
      fadeOutDuration: 300,
    });
    console.log('[Capacitor] Splash screen hidden');

    // Configure status bar
    await configureStatusBar();

    // Set up keyboard listeners
    setupKeyboardListeners();

    // Set up app lifecycle listeners
    setupAppListeners();

    // Initialize push notifications
    await initializePushNotifications(options?.pushHandlers);

    console.log('[Capacitor] All plugins initialized successfully');
  } catch (error) {
    console.error('[Capacitor] Error initializing plugins:', error);
  }
}

/**
 * Configure status bar appearance
 */
async function configureStatusBar(): Promise<void> {
  try {
    // Set status bar style (light content on dark background)
    await StatusBar.setStyle({ style: Style.Light });

    if (isAndroid) {
      // Set background color on Android
      await StatusBar.setBackgroundColor({ color: '#2563eb' });
    }

    console.log('[Capacitor] Status bar configured');
  } catch (error) {
    console.error('[Capacitor] Error configuring status bar:', error);
  }
}

/**
 * Set up keyboard event listeners
 */
function setupKeyboardListeners(): void {
  Keyboard.addListener('keyboardWillShow', (info) => {
    console.log('[Keyboard] Will show, height:', info.keyboardHeight);
    // You can dispatch an event or update state here
    document.body.classList.add('keyboard-visible');
    document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
  });

  Keyboard.addListener('keyboardWillHide', () => {
    console.log('[Keyboard] Will hide');
    document.body.classList.remove('keyboard-visible');
    document.documentElement.style.setProperty('--keyboard-height', '0px');
  });

  // `will*` fires as the animation starts, when the WebView frame is still
  // mid-resize; `did*` fires once it has settled, which is the only point at
  // which window.innerHeight is trustworthy. Re-syncing --app-height here is
  // what stops `body.app-layout` from staying locked to a stale (keyboard-sized
  // or pre-keyboard) height, which otherwise leaves the document taller than
  // the viewport and the fixed nav riding above the real bottom.
  Keyboard.addListener('keyboardDidShow', () => {
    setAppHeight();
  });

  Keyboard.addListener('keyboardDidHide', () => {
    console.log('[Keyboard] Did hide');
    setAppHeight();
    resetStrayDocumentScroll();
  });

  console.log('[Capacitor] Keyboard listeners set up');
}

/**
 * Set up app lifecycle listeners
 */
function setupAppListeners(): void {
  // There is deliberately NO `appStateChange` handler here any more. It used to call a
  // `checkForUpdates()` that ran `registration.update()` and deleted the navigation/asset
  // caches on every foreground — housekeeping for a service worker that native builds no
  // longer have (see `serviceWorkerTeardown.ts`). Updating a native install is Capgo's job.
  // The other foreground concerns live in their own hooks: `useAppBadge`,
  // `useAndroidBackButton` and `useUpdateAvailable` each register their own listener.

  // The Android back button is handled by `useAndroidBackButton` (mounted in
  // App.tsx), which decides from the app's route. It is deliberately NOT
  // handled here: this listener keyed off Capacitor's `canGoBack`, which
  // reflects WebView history and never goes false in this SPA, so the app could
  // never be backed out of. Registering a second listener here would re-create
  // that behaviour, because every `backButton` listener fires.

  // Handle deep links
  App.addListener('appUrlOpen', ({ url }) => {
    console.log('[App] URL opened:', url);

    // Parse the URL and navigate accordingly
    const path = new URL(url).pathname;
    if (path) {
      window.location.href = path;
    }
  });

  console.log('[Capacitor] App listeners set up');
}

/**
 * Haptic feedback utilities
 */
export const haptics = {
  /**
   * Light impact feedback (for selections, toggles)
   */
  light: async (): Promise<void> => {
    if (!isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (e) {
      // Haptics not available
    }
  },

  /**
   * Medium impact feedback (for button presses)
   */
  medium: async (): Promise<void> => {
    if (!isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch (e) {
      // Haptics not available
    }
  },

  /**
   * Heavy impact feedback (for important actions)
   */
  heavy: async (): Promise<void> => {
    if (!isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch (e) {
      // Haptics not available
    }
  },

  /**
   * Success notification feedback
   */
  success: async (): Promise<void> => {
    if (!isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Success });
    } catch (e) {
      // Haptics not available
    }
  },

  /**
   * Warning notification feedback
   */
  warning: async (): Promise<void> => {
    if (!isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Warning });
    } catch (e) {
      // Haptics not available
    }
  },

  /**
   * Error notification feedback
   */
  error: async (): Promise<void> => {
    if (!isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Error });
    } catch (e) {
      // Haptics not available
    }
  },

  /**
   * Selection changed feedback (for pickers, sliders)
   */
  selection: async (): Promise<void> => {
    if (!isNative) return;
    try {
      await Haptics.selectionChanged();
    } catch (e) {
      // Haptics not available
    }
  },
};

/**
 * Status bar utilities
 */
export const statusBar = {
  /**
   * Show the status bar
   */
  show: async (): Promise<void> => {
    if (!isNative) return;
    await StatusBar.show();
  },

  /**
   * Hide the status bar
   */
  hide: async (): Promise<void> => {
    if (!isNative) return;
    await StatusBar.hide();
  },

  /**
   * Set light style (dark text on light background)
   */
  setLight: async (): Promise<void> => {
    if (!isNative) return;
    await StatusBar.setStyle({ style: Style.Light });
  },

  /**
   * Set dark style (light text on dark background)
   */
  setDark: async (): Promise<void> => {
    if (!isNative) return;
    await StatusBar.setStyle({ style: Style.Dark });
  },

  /**
   * Set background color (Android only)
   */
  setColor: async (color: string): Promise<void> => {
    if (!isAndroid) return;
    await StatusBar.setBackgroundColor({ color });
  },
};
